import { FC, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { DeleteSharedNotesModal } from "@/components/modal/delete-shared-notes/delete-shared-notes-modal";
import { AddToCollectionModal } from "@/components/modal/add-to-collection/add-to-collection-modal";
import { MDSFadeBottom, MDSPanelContainer, AppearAnimator } from "@/mds";
import { ShareSheetModal } from "@/components/modal/share-sheet/share-sheet-modal";
import { HomePageNotesList } from "./components/notes-list-page-list";
import { TrackedEvent } from "@/domains/metrics";
import { trackEvent } from "@/domains/metrics";
import styled from "@emotion/styled";
import { ImportedNotesListPageHeader } from "@/pages/notes/components/imported-notes-list-page-header";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { useTabParams } from "@/components/layout/components/tab-bar/use-tab-params";

interface NotesListPageProps {}

export const ImportedNotesListPage: FC<NotesListPageProps> = observer(() => {
  const { importBatchId } = useTabParams();
  const page = useTabPageStore("notesListPage");
  const filters = page.filters;
  page.useEffects();

  useEffectOnMount(() => {
    trackEvent(TrackedEvent.ImportedNotesView, {
      lens: filters.params.lens,
      sort_by: filters.params.sortBy,
      import_batch_id: importBatchId,
    });
  });

  useEffect(() => {
    // the store can take a split second to update based on the filter change
    // so we set the loading state to true proactively to avoid a flash of
    // un-filtered notes
    page.isNotesLoading = true;
    return () => {
      page.isNotesLoading = true;
    };
  }, [page]);

  return (
    <PageWrapper visible={page.subscriptionInitialized}>
      <AddToCollectionModal store={page.addToCollectionModal} />
      <DeleteSharedNotesModal store={page.deleteSharedNotesModal} />
      <ShareSheetModal store={page.shareSheetModal} />
      <ImportedNotesListPageHeader />
      <MDSPanelContainer>
        <HomePageNotesList />
        <MDSFadeBottom />
      </MDSPanelContainer>
    </PageWrapper>
  );
});

ImportedNotesListPage.displayName = "ImportedNotesListPage";

const PageWrapper = styled(AppearAnimator)({
  position: "relative",
  height: "100%",
  display: "flex",
  flexDirection: "column",
});
